/**
 * ss-modal.js — shared, reusable modal shell.
 *
 * <ss-modal> is rendered once (snippets/ss-modal.liquid). Any element with
 * [data-ss-modal-open="{id}"] is a trigger. Two content modes:
 *
 *   1. CLONE (default) — a <template data-ss-modal-content="{id}">; its content is CLONED into the
 *      panel on open (images don't load until then). Used by size-guide + SIIYS.
 *   2. ADOPT — a live element [data-ss-modal-adopt="{id}"]; it is MOVED into the panel on open and
 *      moved back home on close. Used when the content is editable theme blocks that must stay in the
 *      LIVE DOM — the theme editor can't see blocks trapped inside an inert <template>. See
 *      blocks/info-accordion. Moving (vs cloning) also preserves the content's state + listeners.
 *
 * One modal, many triggers — only one open at a time. Centred fade + scale via CSS; focus-trap +
 * Escape + scroll-lock here. Progressive enhancement: no template/adopt target (or no JS) + a trigger
 * that is an <a href> just navigates.
 */
(function () {
  if (customElements.get('ss-modal')) return;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  class SSModal extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-ss-modal-content]');
      this.lastFocused = null;
      this.adopted = null; // the moved-in live node (adopt mode), else null (clone mode)
      this.adoptHome = null; // its original parent, to move it back
      this.adoptNext = null; // its original next sibling, to restore exact order

      this.onDocClick = this.onDocClick.bind(this);
      this.onSelfClick = this.onSelfClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      document.addEventListener('click', this.onDocClick); // triggers (delegated → survive DOM swaps)
      this.addEventListener('click', this.onSelfClick); // overlay + close button
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    onDocClick(e) {
      var trigger = e.target.closest('[data-ss-modal-open]');
      if (!trigger || this.contains(trigger)) return;

      var id = trigger.getAttribute('data-ss-modal-open');
      var esc = window.CSS && CSS.escape ? CSS.escape(id) : id;
      var tpl = document.querySelector('template[data-ss-modal-content="' + esc + '"]');
      var adoptEl = tpl ? null : document.querySelector('[data-ss-modal-adopt="' + esc + '"]');
      if (!tpl && !adoptEl) return; // nothing to show → let an <a href> trigger navigate

      e.preventDefault();
      if (tpl) this.openClone(tpl);
      else this.openAdopted(adoptEl);
    }

    onSelfClick(e) {
      if (e.target.closest('[data-ss-modal-close]')) this.close();
    }

    // CLONE mode — copy a <template>'s content in fresh each open (browser caches images after first).
    openClone(tpl) {
      this.adopted = null;
      this.content.replaceChildren(tpl.content.cloneNode(true));
      this.show();
    }

    // ADOPT mode — MOVE a live element into the panel (appending an existing node relocates it).
    openAdopted(el) {
      this.returnAdopted(); // send any stale node home first (e.g. a fast reopen)
      this.adopted = el;
      this.adoptHome = el.parentNode;
      this.adoptNext = el.nextSibling;
      this.content.replaceChildren(el);
      this.show();
    }

    show() {
      // Was the scroll lock already held by something else (the quick-view drawer, when a modal is
      // opened from inside it)? Then close() must leave it on, or the page behind the still-open
      // drawer starts scrolling. Only read when not already open, so a reopen can't mistake this
      // modal's own lock for someone else's. With nothing else holding it (the PDP) → unchanged.
      if (!this.classList.contains('is-open')) {
        this.lockHeldByOther = document.documentElement.classList.contains('ss-scroll-lock');
      }
      this.lastFocused = document.activeElement;
      this.content.scrollTop = 0;
      this.classList.add('is-open');
      this.removeAttribute('aria-hidden');
      document.documentElement.classList.add('ss-scroll-lock');

      // Focus the dialog container (tabindex="-1") rather than the close button — moves focus into
      // the modal for a11y without flashing a focus ring on the X every open.
      this.focus();
      document.addEventListener('keydown', this.onKeydown);
    }

    // Move an adopted node back to exactly where it came from. No-op in clone mode.
    returnAdopted() {
      if (!this.adopted) return;
      var node = this.adopted;
      var home = this.adoptHome;
      var next = this.adoptNext;
      this.adopted = this.adoptHome = this.adoptNext = null;
      if (!home) return;
      if (next && next.parentNode === home) home.insertBefore(node, next);
      else home.appendChild(node);
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (!this.lockHeldByOther) document.documentElement.classList.remove('ss-scroll-lock');
      document.removeEventListener('keydown', this.onKeydown);

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();

      // After the close transition (matches the 0.35s CSS): send an adopted node home, then clear
      // any cloned content.
      var self = this;
      window.setTimeout(function () {
        if (self.classList.contains('is-open')) return; // reopened during the transition
        self.returnAdopted();
        self.content.replaceChildren();
      }, 350);
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (e.key === 'Tab') this.trapFocus(e);
    }

    trapFocus(e) {
      var nodes = this.querySelectorAll(FOCUSABLE);
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      // We focus the container itself on open (tabindex="-1"); it's not in `nodes`, so an immediate
      // Shift+Tab could escape. Snap focus to an edge instead.
      if (document.activeElement === this) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('ss-modal', SSModal);
})();
